import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Check, X, AlertTriangle, Camera, Upload,
  Shield, ChevronDown, ChevronUp, FileSignature, RefreshCw, FileText, Image as ImageIcon, ExternalLink
} from 'lucide-react';

const STATUS_COLORS = { not_started: 'bg-blue-500', in_progress: 'bg-amber-500', complete: 'bg-emerald-500', blocked: 'bg-red-500' };
const STATUS_LABELS = { not_started: 'Not Started', in_progress: 'In Progress', complete: 'Complete', blocked: 'Blocked' };
const STAGE_NAMES = { progress: { 1: 'Pre-Closure', 2: 'Close-Up & Pre-Finish', 3: 'Completion' }, qa: { 1: 'Pre-Closure QA', 2: 'Close-Up & Pre-Finish QA', 3: 'Completion QA' } };

const QA_BADGE_CLASSES = { pass: 'bg-emerald-600', fail: 'bg-red-600' };
const QA_BADGE_LABELS = { pass: 'Pass', fail: 'Fail' };
const SIGNOFF_BADGE_CLASSES = { approved: 'bg-emerald-500 text-white', rejected: 'bg-red-500 text-white' };
const QA_BG_CLASSES = { fail: 'bg-red-50 border-red-200', pass: 'bg-emerald-50 border-emerald-200' };

function getQABadgeClass(s) { return QA_BADGE_CLASSES[s] || 'bg-blue-500'; }
function getQABadgeLabel(s) { return QA_BADGE_LABELS[s] || 'Pending'; }
function getSignoffBadgeClass(s) { return SIGNOFF_BADGE_CLASSES[s] || 'bg-amber-500 text-white'; }
function formatApiError(err, fb) { const m = err?.response?.data?.detail; return typeof m === 'string' ? m : fb; }

// ── Custom Hook ─────────────────────────────────────────────

function useStageData(unitId, stageType, activeStage) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchStage = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/units/${unitId}/stages/${stageType}/${activeStage}`);
      setData(resp.data);
    } catch { toast.error('Failed to load stage data'); }
    finally { setLoading(false); }
  }, [unitId, stageType, activeStage]);

  useEffect(() => { fetchStage(); }, [fetchStage]);

  const updateItem = useCallback(async (itemId, updates) => {
    try {
      await api.put(`/units/${unitId}/stages/${stageType}/${activeStage}/checklist/${itemId}`, updates);
      fetchStage();
    } catch (err) { toast.error(formatApiError(err, 'Update failed')); }
  }, [unitId, stageType, activeStage, fetchStage]);

  const uploadPhoto = useCallback(async (e, trade) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    const encodedTrade = encodeURIComponent(trade);
    try {
      await api.post(`/units/${unitId}/stages/${stageType}/${activeStage}/photos?trade=${encodedTrade}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo uploaded');
      fetchStage();
    } catch { toast.error('Photo upload failed'); }
    finally { setUploading(false); }
  }, [unitId, stageType, activeStage, fetchStage]);

  const createIssue = useCallback(async (issueForm) => {
    if (!issueForm.description.trim()) { toast.error('Description required'); return false; }
    try {
      await api.post(`/units/${unitId}/stages/${stageType}/${activeStage}/issues`, { ...issueForm, trade: issueForm.trade || 'General' });
      toast.success('Issue raised'); fetchStage(); return true;
    } catch { toast.error('Failed to raise issue'); return false; }
  }, [unitId, stageType, activeStage, fetchStage]);

  const resolveIssue = useCallback(async (issueId) => {
    try { await api.put(`/issues/${issueId}/resolve`, { resolution_notes: 'Resolved' }); toast.success('Issue resolved'); fetchStage(); }
    catch { toast.error('Failed to resolve issue'); }
  }, [fetchStage]);

  const requestSignoff = useCallback(async () => {
    try { await api.post(`/units/${unitId}/stages/${stageType}/${activeStage}/signoff`, { notes: '' }); toast.success('Sign-off requested'); fetchStage(); }
    catch (err) { toast.error(formatApiError(err, 'Sign-off failed')); }
  }, [unitId, stageType, activeStage, fetchStage]);

  const actionSignoff = useCallback(async (action) => {
    if (!data?.signoff?.id) return;
    try { await api.put(`/signoffs/${data.signoff.id}/action`, { action, notes: '' }); toast.success(`Sign-off ${action}ed`); fetchStage(); }
    catch { toast.error(`Failed to ${action} sign-off`); }
  }, [data?.signoff?.id, fetchStage]);

  const signoffTrade = useCallback(async (trade, name) => {
    const encoded = encodeURIComponent(trade);
    try {
      await api.post(`/units/${unitId}/stages/${stageType}/${activeStage}/trades/${encoded}/signoff`, { signoff_name: name });
      toast.success(`${trade} signed off`); fetchStage();
    } catch (err) { toast.error(formatApiError(err, 'Sign-off failed')); }
  }, [unitId, stageType, activeStage, fetchStage]);

  return { data, loading, uploading, fetchStage, updateItem, uploadPhoto, createIssue, resolveIssue, requestSignoff, actionSignoff, signoffTrade };
}

// ── Leaf Components ─────────────────────────────────────────

function PhotoThumb({ photo }) {
  const [src, setSrc] = useState(null);
  const srcRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/photos/${photo.id}`, { responseType: 'blob' }).then(r => {
      if (cancelled) return;
      const url = URL.createObjectURL(r.data); srcRef.current = url; setSrc(url);
    }).catch(() => {});
    return () => { cancelled = true; if (srcRef.current) URL.revokeObjectURL(srcRef.current); };
  }, [photo.id]);
  if (!src) return <div className="w-16 h-16 bg-slate-100 rounded-lg animate-pulse" />;
  return <img src={src} alt={photo.original_filename} className="w-16 h-16 object-cover rounded-lg border border-slate-200" data-testid={`photo-thumb-${photo.id}`} />;
}

function ProgressItem({ item, onUpdate, canEdit, onJumpToQA }) {
  const isComplete = item.status === 'complete';
  const isQALink = /qa form completed/i.test(item.item_name || '');
  return (
    <div className="flex items-start gap-3 p-2.5 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`checklist-item-${item.id}`}>
      <Checkbox checked={isComplete} onCheckedChange={() => canEdit && onUpdate(item.id, { status: isComplete ? 'pending' : 'complete' })} disabled={!canEdit} className="mt-0.5" data-testid={`check-${item.id}`} />
      <div className="flex-1 min-w-0">
        {isQALink ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onJumpToQA?.(); }}
            className={`text-sm text-left inline-flex items-center gap-1 font-medium transition-colors ${isComplete ? 'text-slate-400 line-through' : 'text-blue-600 hover:text-blue-700 hover:underline'}`}
            data-testid={`qa-link-${item.id}`}
          >
            {item.item_name}
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
          </button>
        ) : (
          <span className={`text-sm ${isComplete ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{item.item_name}</span>
        )}
        {item.updated_by_name && <p className="text-[10px] text-slate-400 mt-0.5">by {item.updated_by_name} {item.updated_at ? new Date(item.updated_at).toLocaleString() : ''}</p>}
      </div>
    </div>
  );
}

function QAItem({ item, onUpdate, canEdit }) {
  const [expanded, setExpanded] = useState(item.status === 'fail');
  const [failComment, setFailComment] = useState(item.fail_comment || '');
  const [fonnConfirmed, setFonnConfirmed] = useState(item.fonn_confirmed || false);
  const isPass = item.status === 'pass';
  const isFail = item.status === 'fail';
  const bgClass = QA_BG_CLASSES[item.status] || 'bg-slate-50 border-slate-200';

  const handleFail = () => {
    if (!failComment.trim()) { toast.error('Comment required'); return; }
    onUpdate(item.id, { status: 'fail', fail_comment: failComment, fonn_confirmed: fonnConfirmed });
  };

  return (
    <div className={`p-2.5 border rounded-lg ${bgClass}`} data-testid={`qa-item-${item.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-slate-800 flex-1">{item.item_name}</span>
        <div className="flex items-center gap-1">
          {canEdit ? (
            <>
              <Button size="sm" variant={isPass ? 'default' : 'outline'} className={`h-6 px-2 text-[11px] ${isPass ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`} onClick={() => onUpdate(item.id, { status: 'pass' })} data-testid={`pass-${item.id}`}><Check className="h-3 w-3 mr-0.5" />Pass</Button>
              <Button size="sm" variant={isFail ? 'default' : 'outline'} className={`h-6 px-2 text-[11px] ${isFail ? 'bg-red-600 hover:bg-red-700' : ''}`} onClick={() => setExpanded(!expanded)} data-testid={`fail-toggle-${item.id}`}><X className="h-3 w-3 mr-0.5" />Fail</Button>
            </>
          ) : (
            <Badge className={`text-[10px] text-white ${getQABadgeClass(item.status)}`}>{getQABadgeLabel(item.status)}</Badge>
          )}
        </div>
      </div>
      {expanded && canEdit && (
        <div className="mt-2 space-y-2 pl-2 border-l-2 border-red-300">
          <Textarea value={failComment} onChange={e => setFailComment(e.target.value)} placeholder="Describe the issue (required)..." className="text-sm min-h-[50px]" data-testid={`fail-comment-${item.id}`} />
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={fonnConfirmed} onCheckedChange={setFonnConfirmed} data-testid={`fonn-checkbox-${item.id}`} />
            <span className="text-xs text-slate-700">Issue raised in Fonn</span>
          </label>
          <Button size="sm" onClick={handleFail} className="bg-red-600 hover:bg-red-700 text-xs" data-testid={`confirm-fail-${item.id}`}>Confirm Fail</Button>
        </div>
      )}
      {item.updated_by_name && <p className="text-[10px] text-slate-400 mt-1">by {item.updated_by_name}</p>}
    </div>
  );
}

function IssueCard({ issue, onResolve, canResolve }) {
  const isOpen = issue.status === 'open';
  return (
    <div className={`p-3 border rounded-lg ${isOpen ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`} data-testid={`issue-${issue.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <p className="text-sm text-slate-800 font-medium">{issue.description}</p>
          <p className="text-[10px] text-slate-500 mt-1">{issue.trade} - by {issue.raised_by_name} {issue.raised_in_fonn && <span className="text-emerald-600">(Fonn)</span>}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`text-[10px] ${isOpen ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'}`}>{issue.status}</Badge>
          {isOpen && canResolve && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onResolve(issue.id)} data-testid={`resolve-issue-${issue.id}`}>Resolve</Button>}
        </div>
      </div>
    </div>
  );
}

// ── Trade Checklist with Sign-off ───────────────────────────

function TradeChecklist({ trade, tradeData, isQA, isExpanded, canEdit, onToggle, onItemUpdate, onUploadPhoto, onSignoffTrade, uploading, onJumpToQA }) {
  const [signoffName, setSignoffName] = useState('');
  const items = tradeData.items || [];
  const photos = tradeData.photos || [];
  const tsignoff = tradeData.signoff;
  const photoCount = tradeData.photo_count || 0;
  const canSignoff = tradeData.can_signoff;
  const completed = items.filter(i => ['complete', 'pass', 'na'].includes(i.status)).length;
  const total = items.length;
  const pct = total > 0 ? (completed / total) * 100 : 0;
  const isStageTrade = trade.includes('Stage Completion') || trade.includes('Declaration') || trade.includes('SMT');
  const packageName = tradeData.package || trade;

  const handleTradeSignoff = () => {
    if (!signoffName.trim()) { toast.error('Enter sign-off name'); return; }
    onSignoffTrade(trade, signoffName);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden trade-section" data-testid={`trade-section-${trade.replace(/[\s/]+/g, '-').toLowerCase()}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50 transition-colors" data-testid={`trade-toggle-${trade.replace(/[\s/]+/g, '-').toLowerCase()}`}>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-slate-900">{trade}</span>
            <span className="text-xs text-slate-500">{completed}/{total}</span>
            {photoCount > 0 && <Badge variant="outline" className="text-[9px] gap-0.5"><ImageIcon className="h-2.5 w-2.5" />{photoCount}</Badge>}
            {tsignoff && <Badge className="bg-emerald-100 text-emerald-700 text-[9px]"><Check className="h-2.5 w-2.5 mr-0.5" />Signed</Badge>}
            {!canEdit && <Badge variant="outline" className="text-[9px]">View Only</Badge>}
          </div>
          {packageName !== trade && <p className="text-[10px] text-slate-400 mt-0.5">{packageName}</p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="w-14 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
        </div>
      </button>
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2">
          {items.map(item => isQA
            ? <QAItem key={item.id} item={item} onUpdate={onItemUpdate} canEdit={canEdit} />
            : <ProgressItem key={item.id} item={item} onUpdate={onItemUpdate} canEdit={canEdit} onJumpToQA={onJumpToQA} />
          )}

          {/* Trade Photos */}
          {!isStageTrade && (
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <Camera className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-xs text-slate-500">{photoCount} photo{photoCount !== 1 ? 's' : ''}</span>
                {photoCount === 0 && <span className="text-[10px] text-red-500 font-medium">Required</span>}
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={e => onUploadPhoto(e, trade)} disabled={uploading} data-testid={`photo-upload-${trade.replace(/[\s/]+/g, '-').toLowerCase()}`} />
                <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={uploading} asChild>
                  <span><Upload className="h-2.5 w-2.5 mr-1" />{uploading ? '...' : 'Photo'}</span>
                </Button>
              </label>
            </div>
          )}
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-1.5">{photos.map(p => <PhotoThumb key={p.id} photo={p} />)}</div>
          )}

          {/* Trade Sign-off */}
          {!isStageTrade && !tsignoff && (
            <div className="pt-2 border-t border-slate-100 space-y-2">
              <div className="flex items-center gap-2">
                <Input value={signoffName} onChange={e => setSignoffName(e.target.value)} placeholder="Sign-off name..." className="h-7 text-xs flex-1" data-testid={`signoff-name-${trade.replace(/[\s/]+/g, '-').toLowerCase()}`} />
                <Button size="sm" className="h-7 text-[10px] bg-blue-600 hover:bg-blue-700" onClick={handleTradeSignoff} disabled={!canSignoff} data-testid={`signoff-btn-${trade.replace(/[\s/]+/g, '-').toLowerCase()}`}>
                  <FileSignature className="h-3 w-3 mr-1" />Sign Off
                </Button>
              </div>
              {!canSignoff && <p className="text-[10px] text-amber-600">Complete all items and upload at least 1 photo to sign off</p>}
            </div>
          )}
          {tsignoff && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-[10px] text-emerald-700"><Check className="inline h-3 w-3 mr-1" />Signed off by <b>{tsignoff.signoff_name}</b> ({tsignoff.signed_off_by_name}) on {new Date(tsignoff.signed_off_at).toLocaleDateString()}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Section Components ──────────────────────────────────────

function WarningsBanner({ warnings }) {
  if (!warnings.length) return null;
  return (
    <Alert className="bg-amber-50 border-amber-200" data-testid="stage-warnings">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertDescription className="text-sm text-amber-800">{warnings.map(w => <span key={w} className="block">{w}</span>)}</AlertDescription>
    </Alert>
  );
}

function IssuesSection({ issues, issueOpen, setIssueOpen, issueForm, setIssueForm, onSubmitIssue, onResolve, isAdmin }) {
  const openCount = issues.filter(i => i.status === 'open').length;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="issues-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm text-slate-900" style={{ fontFamily: 'Outfit' }}><AlertTriangle className="inline h-4 w-4 mr-1.5 text-slate-500" />Issues ({openCount} open)</h3>
        <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="raise-issue-button"><AlertTriangle className="h-3 w-3 mr-1" />Raise Issue</Button></DialogTrigger>
          <DialogContent data-testid="issue-dialog">
            <DialogHeader><DialogTitle>Raise an Issue</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1"><Label className="text-xs">Trade</Label><Input value={issueForm.trade} onChange={e => setIssueForm({...issueForm, trade: e.target.value})} placeholder="e.g. Plumbing" data-testid="issue-trade-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Description</Label><Textarea value={issueForm.description} onChange={e => setIssueForm({...issueForm, description: e.target.value})} placeholder="Describe..." data-testid="issue-description-input" /></div>
              <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={issueForm.raised_in_fonn} onCheckedChange={v => setIssueForm({...issueForm, raised_in_fonn: v})} data-testid="issue-fonn-checkbox" /><span className="text-sm text-slate-700">Raised in Fonn</span></label>
              <Button onClick={onSubmitIssue} className="w-full bg-red-600 hover:bg-red-700" data-testid="submit-issue-button">Submit Issue</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {issues.length > 0 ? <div className="space-y-2">{issues.map(issue => <IssueCard key={issue.id} issue={issue} onResolve={onResolve} canResolve={isAdmin} />)}</div> : <p className="text-xs text-slate-400">No issues</p>}
    </div>
  );
}

function SignoffSection({ signoff, onRequestSignoff, onSignoffAction, isAdmin }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="signoff-section">
      <h3 className="font-semibold text-sm text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}><FileSignature className="inline h-4 w-4 mr-1.5 text-slate-500" />Stage Sign-off</h3>
      {signoff ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2"><Badge className={`text-[10px] ${getSignoffBadgeClass(signoff.status)}`}>{signoff.status}</Badge><span className="text-xs text-slate-500">by {signoff.requested_by_name}</span></div>
          {signoff.status === 'pending' && isAdmin && (
            <div className="flex gap-2">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs" onClick={() => onSignoffAction('approve')} data-testid="approve-signoff-button"><Check className="h-3 w-3 mr-1" />Approve</Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 text-xs" onClick={() => onSignoffAction('reject')} data-testid="reject-signoff-button"><X className="h-3 w-3 mr-1" />Reject</Button>
            </div>
          )}
        </div>
      ) : <Button size="sm" onClick={onRequestSignoff} className="bg-blue-600 hover:bg-blue-700 text-xs" data-testid="request-signoff-button"><FileSignature className="h-3 w-3 mr-1" />Request Stage Sign-off</Button>}
    </div>
  );
}

function AdminOverrideSection({ stage, unitId, stageType, activeStage, onRefresh }) {
  const handleOverride = useCallback(async (s) => {
    try { await api.put(`/units/${unitId}/stages/${stageType}/${activeStage}/status`, { status: s }); toast.success(`Status set to ${STATUS_LABELS[s]}`); onRefresh(); }
    catch { toast.error('Override failed'); }
  }, [unitId, stageType, activeStage, onRefresh]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="status-override-section">
      <h3 className="font-semibold text-sm text-slate-900 mb-3" style={{ fontFamily: 'Outfit' }}><Shield className="inline h-4 w-4 mr-1.5 text-slate-500" />Admin Override</h3>
      <div className="flex flex-wrap gap-2">
        {['not_started', 'in_progress', 'complete', 'blocked'].map(s => (
          <Button key={s} size="sm" variant="outline" className={`text-xs ${stage.status === s ? 'ring-2 ring-blue-500' : ''}`} onClick={() => handleOverride(s)} data-testid={`override-${s}`}>
            <div className={`w-2 h-2 rounded-full mr-1.5 ${STATUS_COLORS[s]}`} />{STATUS_LABELS[s]}
          </Button>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────

export default function StageView() {
  const { unitId, stageType } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const initialStage = ['1', '2', '3'].includes(searchParams.get('stage')) ? searchParams.get('stage') : '1';
  const [activeStage, setActiveStage] = useState(initialStage);
  const [expandedTrades, setExpandedTrades] = useState({});
  const [issueForm, setIssueForm] = useState({ description: '', trade: '', raised_in_fonn: false });
  const [issueOpen, setIssueOpen] = useState(false);

  // Sync active stage whenever the URL query param changes (e.g. clicking QA deep link
  // while already on this screen)
  useEffect(() => {
    const q = searchParams.get('stage');
    if (['1', '2', '3'].includes(q) && q !== activeStage) setActiveStage(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { data, loading, uploading, fetchStage, updateItem, uploadPhoto, createIssue, resolveIssue, requestSignoff, actionSignoff, signoffTrade } = useStageData(unitId, stageType, activeStage);

  const isAdmin = useMemo(() => ['admin', 'project_manager', 'site_manager'].includes(user?.role), [user?.role]);
  const isQA = stageType === 'qa';

  const jumpToQA = useCallback(() => {
    // Navigate to the matching QA stage for this unit, pre-selecting the current stage number
    navigate(`/units/${unitId}/qa?stage=${activeStage}`);
  }, [navigate, unitId, activeStage]);

  const canEditTrade = useCallback((trade, tradeData) => {
    if (user?.role === 'subcontractor') {
      const pkg = tradeData?.package || trade;
      return (user?.packages || []).includes(pkg);
    }
    return true;
  }, [user?.role, user?.packages]);

  useEffect(() => {
    if (data?.trades) {
      const initial = {};
      Object.keys(data.trades).forEach(t => { initial[t] = true; });
      setExpandedTrades(initial);
    }
  }, [data?.trades]);

  const handleIssueSubmit = useCallback(async () => {
    const success = await createIssue(issueForm);
    if (success) { setIssueOpen(false); setIssueForm({ description: '', trade: '', raised_in_fonn: false }); }
  }, [issueForm, createIssue]);

  const toggleTrade = useCallback((trade) => setExpandedTrades(prev => ({ ...prev, [trade]: !prev[trade] })), []);

  const handleDownloadPdf = async () => {
    try {
      const resp = await api.get(`/reports/unit/${unitId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a'); a.href = url; a.download = `${data?.unit?.name || 'unit'}_report.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF generation failed'); }
  };

  if (loading && !data) return <Layout><div className="flex items-center justify-center py-16 text-slate-400">Loading...</div></Layout>;

  const stage = data?.stage || {};
  const trades = data?.trades || {};
  const issues = data?.issues || [];
  const signoff = data?.signoff;
  const warnings = data?.warnings || [];
  const unitName = data?.unit?.name || '';

  return (
    <Layout>
      <div className="space-y-4" data-testid="stage-view-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>{unitName} - {isQA ? 'QA' : 'Progress'}</h1>
            <Badge className={`text-[10px] text-white mt-1 ${STATUS_COLORS[stage.status]}`}>{STATUS_LABELS[stage.status]}</Badge>
          </div>
          <Button size="sm" variant="outline" className="text-xs" onClick={handleDownloadPdf} data-testid="download-pdf-button"><FileText className="h-3 w-3 mr-1" />PDF</Button>
        </div>

        <Tabs value={activeStage} onValueChange={setActiveStage} data-testid="stage-tabs">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="1" data-testid="stage-tab-1">Stage 1</TabsTrigger>
            <TabsTrigger value="2" data-testid="stage-tab-2">Stage 2</TabsTrigger>
            <TabsTrigger value="3" data-testid="stage-tab-3">Stage 3</TabsTrigger>
          </TabsList>
        </Tabs>

        <p className="text-sm font-medium text-slate-600">{STAGE_NAMES[stageType]?.[parseInt(activeStage)]}</p>
        <WarningsBanner warnings={warnings} />

        <div className="space-y-3">
          {Object.entries(trades).map(([trade, tradeData]) => (
            <TradeChecklist key={trade} trade={trade} tradeData={tradeData} isQA={isQA} isExpanded={expandedTrades[trade]} canEdit={canEditTrade(trade, tradeData)} onToggle={() => toggleTrade(trade)} onItemUpdate={updateItem} onUploadPhoto={uploadPhoto} onSignoffTrade={signoffTrade} uploading={uploading} onJumpToQA={jumpToQA} />
          ))}
        </div>

        <Separator />
        <IssuesSection issues={issues} issueOpen={issueOpen} setIssueOpen={setIssueOpen} issueForm={issueForm} setIssueForm={setIssueForm} onSubmitIssue={handleIssueSubmit} onResolve={resolveIssue} isAdmin={isAdmin} />
        <SignoffSection signoff={signoff} onRequestSignoff={requestSignoff} onSignoffAction={actionSignoff} isAdmin={isAdmin} />
        {isAdmin && <AdminOverrideSection stage={stage} unitId={unitId} stageType={stageType} activeStage={activeStage} onRefresh={fetchStage} />}

        <div className="text-center pb-4">
          <Button variant="ghost" size="sm" onClick={fetchStage} className="text-xs text-slate-500" data-testid="refresh-stage-button"><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </div>
      </div>
    </Layout>
  );
}
